/* ============================================================
   Kit builder — generator layer
   ------------------------------------------------------------
   Sits between recipe-engine.js (position maps) and the theme
   section (DOM). Pure assembly: takes the embedded kit data,
   applies every recipe to every base colourway for every
   included yarn, dresses survivors in purchasable-kit shape
   (variant ids, prices, buddy yarn lines), prices them, sorts.

   No DOM, no Shopify calls — node-testable like the engine.

   2026-10-22 (Clew, with Lisa) — kit-simplify: the relationship
   walker is gone (git history before kit-simplify has it, with
   decisions G1, G6-interleave and G8). Every recipe is a
   position map now; old walker recipes are listed in ONE console
   warning per kit as "needs converting".

   INPUT data shape (built by sections/custom-kit-colourworks.liquid):
   {
     kitHandle, swatchPattern,
     meterage:      [ ["350","300",...], ... ]   // per size, per slot (metres)
     buddyMeterage: [ ["120","100",...], ... ] | null,
     recipes:        [ "Z0;Z2;Z4", "Z4;Z2;Z0", ... ],
     baseColourways: [ "sea glass", ... ],        // curated, light→dark each
     families: { title: { title, swatch:[{name,hex}] } },   // base colourways only
     yarns: { productId: {
         productId, title, handle, vendor, unitMeterage,
         matchGroups: [ ["412 pink","2582 after dark",...], ... ],   // G10
         variants: { colourName: { variantId, price, inventory, image,
                                   pairing:[{variantId,title,productTitle,
                                             price,inventory,unitMeterage,image}] } }
     } },
     buddyYarns: [ { productId, title, unitMeterage,
                     variants: { colourName:{variantId,price,inventory,image} } } ]
   }

   OUTPUT of generateSets(data, sizeIndex, Engine):
   [ { hard: true, name, score: null, warnings[], base,
       yarn: { productId, title, handle },
       colours: [ { slotRef, family, index, name, hex,
                    variantId, price, inventory, units, offsetFrom?,
                    buddy: { variantId, title, productTitle,
                             price, inventory, units, image } | null } ],
       price } ]                                  // cents

   DECISIONS (veto any):
   G2. (REVISED 2026-10-22) Every recipe applies to EVERY base
       colourway on the kit, in listing order. The old `fixed`
       gate existed to keep relationship gradients out of Z paths;
       with no walker there are no gradients, so the curated list
       is the designation. Missing base colourways are named.
   G3. A colour name with no matching variant on a yarn = stock 0
       for that yarn — silently. Colourways are tied to a base, so
       a colourway built for another base mostly comes up empty
       here and is skipped by the engine (S5); that's expected.
   G4. Buddy resolution per slot: kit.buddy_yarn products in list
       order, exact title; then TRANSLATE (G10); then the MAIN
       variant's custom.mohair_pairing list in order. Buddy
       coverage is passed to the engine as slotOk, so a colour
       whose buddy strand can't be covered is simply unavailable
       and the shifting rule routes around it. pushSet's prune
       stays as a backstop.
   G5. Dedupe key = yarn productId + main variant ids, in slot
       order (slot order is chart position).
   G6. (REVISED 2026-10-22) Sort: base colourway listing order,
       then recipe order, then price asc. Display cap is the
       section's job — everything survivable is returned.
   G7. A recipe whose token count differs from the meterage slot
       count is skipped with a console warning — data error on
       the kit entry, not a resolvable state.
   G9. (2026-07-13) Buddy lines carry the variant `image` from
       either source so the photo renderer can show held-with
       strands.
   G10. (2026-07-14) GROUP-TRANSLATED BUDDY MATCH. Between the
       exact-title rung and the pairing rung, resolveBuddy consults
       colour_match_group labels (embedded per yarn as matchGroups):
       a buddy variant titled with ANY label in the main colour's
       group satisfies the slot. First group containing the label
       wins (the shelf's find() convention), cached on data. Rung
       order exact > translated > pairing; a translated line is
       flagged `translated: true`.
   G11. (2026-10-22) SHARED SLOTS (=Zn; engine S6/S7). Lisa: a
       repeat is most likely a typo, but can be deliberate (a
       design for 5 colours kitted with 4), so the deliberate one
       is marked. Slots sharing a colour share one buddy strand
       too: slotOk and pushSet hand resolveBuddy all their metres
       (an array) and the buddy must cover them together (G12).
       An unmarked repeat is warned about once per kit (the
       message carries the marked version); a lone =Zn is warned
       about and read as Zn.
   G12. (2026-10-22) METRES FIRST (Lisa). A variant used in more
       than one slot (a shared =Zn colour, a customer picking one
       colour twice, two colours whose buddy is the same mohair)
       adds up its metres and rounds up once: 120 m + 80 m of
       100 m balls is 2 balls, not 2 + 1. poolNeeds() does the sum
       for the page's totals, stock check and cart, and for the
       set price here; the engine's group stock (groupUnits) and
       resolveBuddy pool the same way. Each slot keeps its own
       `units` (what it alone would need), now only a fallback.
   G13. (2026-10-22) THE KIT LIST (Lisa). kitLines() turns the
       pooled needs into the page's "included in this kit" lines:
       one per yarn, count after the name ("7 gold ×1"), and
       "(used twice)" when one yarn serves several slots.
   ============================================================ */

'use strict';

function autoName(colours, maxLen) {
  const full = colours.map(c => c.name).join(' / ');
  const cap = maxLen || 100;
  return full.length > cap ? full.slice(0, cap - 1).trimEnd() + '…' : full;
}

// G10: label -> its group's full label list (first group containing the
// label wins, matching the shelf's find() convention). Built lazily from
// every yarn's matchGroups, cached on data.
function groupLabelsFor(data, colourName) {
  if (!data.__labelGroups) {
    const map = {};
    for (const pid of Object.keys(data.yarns || {})) {
      for (const labels of (data.yarns[pid].matchGroups || [])) {
        for (const l of labels) {
          if (!(l in map)) map[l] = labels;
        }
      }
    }
    data.__labelGroups = map;
  }
  return data.__labelGroups[colourName] || null;
}

// one buddy line for one slot, or null. For slots sharing one colour (G11)
// buddyMetres is an array, one entry per slot: the buddy must then cover
// their units together, and the line carries `unitsEach` for pushSet.
function resolveBuddy(data, mainVariant, colourName, buddyMetres) {
  const reqs = (Array.isArray(buddyMetres) ? buddyMetres : [buddyMetres]).map(m => parseFloat(m) || 0);
  if (!reqs.length || reqs.some(r => r <= 0)) return null;
  // what one candidate must cover: the slots' metres pooled and rounded
  // up once (G12); `each` is what each slot alone would need
  const total = reqs.reduce((sum, r) => sum + r, 0);
  const need = unitMeterage => ({
    each: reqs.map(r => Math.ceil(r / (unitMeterage || 1))),
    units: Math.ceil(total / (unitMeterage || 1))
  });
  const line = (fields, n) => (reqs.length > 1 ? Object.assign(fields, { unitsEach: n.each }) : fields);

  // rung 1: exact title on a buddy_yarn product (list order = preference)
  for (const b of (data.buddyYarns || [])) {
    const v = b.variants[colourName];
    if (!v) continue;
    const n = need(b.unitMeterage);
    if (v.inventory >= n.units) {
      return line({
        variantId: v.variantId, title: colourName, productTitle: b.title,
        price: v.price, inventory: v.inventory, units: n.units,
        unitMeterage: b.unitMeterage || 1,                     // G12
        image: v.image || ''                                   // G9
      }, n);
    }
  }

  // rung 2 (G10): group-translated title on a buddy_yarn product.
  // Same buddy-list-order preference; within one buddy yarn, group
  // label order decides ties (the group is small and hand-curated).
  const groupLabels = groupLabelsFor(data, colourName);
  if (groupLabels) {
    for (const b of (data.buddyYarns || [])) {
      for (const label of groupLabels) {
        if (label === colourName) continue;        // rung 1 already tried it
        const v = b.variants[label];
        if (!v) continue;
        const n = need(b.unitMeterage);
        if (v.inventory >= n.units) {
          return line({
            variantId: v.variantId, title: label, productTitle: b.title,
            price: v.price, inventory: v.inventory, units: n.units,
            unitMeterage: b.unitMeterage || 1,                 // G12
            image: v.image || '',                              // G9
            translated: true                                   // G10
          }, n);
        }
      }
    }
  }

  // rung 3: hand-set pairing list on the main variant (exact by nature)
  for (const p of (mainVariant.pairing || [])) {
    const n = need(p.unitMeterage);
    if (p.inventory >= n.units) {
      return line({
        variantId: p.variantId, title: p.title, productTitle: p.productTitle,
        price: p.price, inventory: p.inventory, units: n.units,
        unitMeterage: p.unitMeterage || 1,                     // G12
        image: p.image || ''                                   // G9
      }, n);
    }
  }
  return null;
}

// G12: what a kit needs, per variant, metres first. picks: by slot, null
// where nothing is chosen yet; each { variantId, price, inventory, name,
// units, buddy? }, buddy lines as resolveBuddy makes them. metres and
// buddyMetres: the size's rows; unitMeterage: the main yarn's metres per
// unit. Returns a Map, variantId → { units, metres, price, inventory,
// name, slots, firstSlot }, in first-use order (`slots`: every slot that
// uses the variant, in order). With no metres to pool (no row,
// or a line without unitMeterage) a variant adds up its slots' units.
// Buddy lines count only where the size has a buddy row.
function poolNeeds(picks, metres, buddyMetres, unitMeterage) {
  const out = new Map();
  const add = (line, m, perUnit, name, slot) => {
    let e = out.get(line.variantId);
    if (!e) {
      e = { units: 0, metres: 0, perUnit: perUnit || 0, slotUnits: 0,
            price: line.price, inventory: line.inventory, name, slots: [], firstSlot: slot };
      out.set(line.variantId, e);
    }
    e.metres += parseFloat(m) || 0;
    e.slotUnits += line.units || 0;
    if (!e.slots.includes(slot)) e.slots.push(slot);
  };
  (picks || []).forEach((p, i) => {
    if (!p) return;
    add(p, (metres || [])[i], unitMeterage, p.name, i);
    if (p.buddy && buddyMetres) add(p.buddy, buddyMetres[i], p.buddy.unitMeterage,
      p.buddy.title + ' (' + p.buddy.productTitle + ')', i);
  });
  for (const e of out.values()) {
    e.units = e.perUnit > 0 && e.metres > 0 ? Math.ceil(e.metres / e.perUnit) : e.slotUnits;
    delete e.perUnit;
    delete e.slotUnits;
  }
  return out;
}

// G13: the page's "included in this kit" list, as lines. One line per
// yarn at its first use; count after the name, since names start with
// the shade number ("1 white ×2"); "(used twice)" when a yarn serves more
// than one slot, so nobody reads two lines as two balls (Lisa,
// 2026-10-22). Returns, in slot order, { slot, empty: true } for a slot
// not chosen yet, or { slot, text, buddies: [text], buddyItems: [buddy],
// missingBuddy } at a yarn's first use (buddyItems parallels buddies).
// A yarn's later slots are left out; their buddy
// strands are listed under it, each once, at the buddy's own first use.
function kitLines(picks, needs, hasBuddyRow, baseTitle) {
  const times = k => (k === 2 ? 'used twice' : 'used ' + k + ' times');
  const qty = n => '×' + n.units + (n.slots.length > 1 ? ' (' + times(n.slots.length) + ')' : '');
  // the base yarn's name after the colour: "1 white (united socks & more) ×2"
  // (Lisa, 2026-10-27). Optional, so callers without it get the old line.
  const base = baseTitle ? ' (' + baseTitle + ')' : '';
  const out = [];
  (picks || []).forEach((p, i) => {
    if (!p) { out.push({ slot: i, empty: true }); return; }
    const n = needs.get(p.variantId);
    if (!n || n.slots[0] !== i) return;
    const line = { slot: i, text: p.name + base + ' ' + qty(n), buddies: [], buddyItems: [], missingBuddy: false };
    if (hasBuddyRow) {
      const seen = new Set();
      for (const s of n.slots) {
        const b = picks[s] && picks[s].buddy;
        if (!b) { line.missingBuddy = true; continue; }
        const nb = needs.get(b.variantId);
        if (!nb || seen.has(b.variantId) || !n.slots.includes(nb.slots[0])) continue;
        seen.add(b.variantId);
        line.buddies.push('+ ' + b.title + ' ' + b.productTitle + ' ' + qty(nb));
        line.buddyItems.push(b);   // same order as buddies: the page shows each one's photo
      }
    }
    out.push(line);
  });
  return out;
}

// engine colourways for one yarn: same palettes, this yarn's stock (G3)
function familiesForYarn(data, yarn) {
  const out = {};
  for (const t of Object.keys(data.families)) {
    const f = data.families[t];
    out[t] = {
      title: f.title,
      swatch: f.swatch.map(c => ({
        name: c.name,
        hex: c.hex,
        stock: yarn.variants[c.name] ? yarn.variants[c.name].inventory : 0
      }))
    };
  }
  return out;
}

function generateSets(data, sizeIndex, Engine) {
  const out = [];

  const metresRow = (data.meterage || [])[sizeIndex];
  if (!metresRow || !metresRow.length) return out;
  const buddyRow = data.buddyMeterage ? data.buddyMeterage[sizeIndex] : null;
  const hasBuddy = !!(buddyRow && buddyRow.length);

  if ((data.buddyYarns || []).length && !hasBuddy) {
    console.warn('[kit-generator] buddy yarn(s) configured but buddy meterage for size ' + sizeIndex +
      ' is missing/empty — generating WITHOUT the buddy strand. Check buddy_yarn_meterage has one row per size.');
  }

  // parse recipes once — yarn-independent (G7). Old walker recipes
  // are collected and named in ONE warning: that's the migration list.
  const parsed = [];
  const needsConverting = [];
  (data.recipes || []).forEach((recipe, recipeIdx) => {
    let tokens;
    try { tokens = Engine.parseRecipe(recipe); }
    catch (e) {
      if (/needs converting/.test(e.message)) needsConverting.push(recipe);
      else console.warn('[kit-generator] kit "' + data.kitHandle + '": recipe "' + recipe + '" skipped — ' + e.message);
      return;
    }
    if (tokens.length !== metresRow.length) {                       // G7
      console.warn('kit-generator: recipe "' + recipe + '" has ' + tokens.length +
        ' slots but meterage has ' + metresRow.length + ' — skipped');
      return;
    }
    // G11: a lone =Zn has nothing to share with; harmless, but say so
    const lone = tokens.filter(t => t.shared && tokens.every(u => u === t || u.index !== t.index));
    if (lone.length) {
      console.warn('[kit-generator] kit "' + data.kitHandle + '": recipe "' + recipe + '": ' +
        lone.map(t => '=Z' + t.index).join(', ') + ' has no other slot to share with, so it reads as ' +
        lone.map(t => 'Z' + t.index).join(', ') + ' (a typo?)');
    }
    parsed.push({ recipe, recipeIdx });
  });
  if (needsConverting.length) {
    console.warn('[kit-generator] kit "' + data.kitHandle + '" still has walker-style recipe(s) — ' +
      'needs converting to position maps (Z0;Z2;...): ' + needsConverting.join('  |  '));
  }

  // ---- G2 (revised): every base colourway is a target ----
  const targets = [];
  for (const t of (data.baseColourways || [])) {
    if (!data.families[t]) {
      console.warn('[kit-generator] base colourway "' + t + '" is not in the embedded ' +
        'colourways — the entry may have been renamed or deleted, or its reference is broken.');
      continue;
    }
    targets.push(t);
  }
  if (parsed.length && !targets.length) {
    console.warn('[kit-generator] kit "' + data.kitHandle + '" has recipes but no usable base colourways — no sets can generate.');
  }

  // dress one engine result in purchasable-kit shape; prune on any gap
  function pushSet(result, yarn, base, recipeIdx, unitsPerSlot) {
    // G11: slots sharing one colour share one buddy, resolved for them together
    const sharedBuddy = new Map();                          // slot → buddy line
    if (hasBuddy) {
      const slotsAt = new Map();                            // picked position → slots
      result.colours.forEach((c, i) => slotsAt.set(c.index, [...(slotsAt.get(c.index) || []), i]));
      for (const slots of slotsAt.values()) {
        if (slots.length < 2) continue;
        const c = result.colours[slots[0]];
        const v = yarn.variants[c.name];
        const b = v && resolveBuddy(data, v, c.name, slots.map(s => buddyRow[s]));
        if (!b) return;                                     // backstop, as below
        const { unitsEach, ...one } = b;
        slots.forEach((s, k) => sharedBuddy.set(s, Object.assign({}, one, { units: unitsEach[k] })));
      }
    }

    const colours = [];
    for (let i = 0; i < result.colours.length; i++) {
      const c = result.colours[i];
      const v = yarn.variants[c.name];
      if (!v) return;                       // shouldn't happen (stock came from v), belt & braces
      const col = {
        slotRef: c.slotRef, family: c.family, index: c.index,
        name: c.name, hex: c.hex,
        variantId: v.variantId, price: v.price, inventory: v.inventory,
        units: unitsPerSlot[i],
        buddy: null
      };
      if (c.offsetFrom != null) col.offsetFrom = c.offsetFrom;
      if (hasBuddy) {
        col.buddy = sharedBuddy.get(i) || resolveBuddy(data, v, c.name, buddyRow[i]);
        if (!col.buddy) return;             // G4 backstop (qualifier should prevent this)
      }
      colours.push(col);
    }

    // G12: priced as the page will price it, metres pooled per variant
    let price = 0;
    for (const n of poolNeeds(colours, metresRow, buddyRow, yarn.unitMeterage).values()) {
      price += n.price * n.units;
    }

    out.push({
      hard: true,
      name: base,
      base,
      recipeIdx,
      score: result.score,
      warnings: result.warnings || [],
      yarn: { productId: yarn.productId, title: yarn.title, handle: yarn.handle },
      colours,
      price
    });
  }

  for (const pid of Object.keys(data.yarns || {})) {
    const yarn = data.yarns[pid];
    const unitsPerSlot = metresRow.map(m =>
      Math.ceil((parseFloat(m) || 0) / (yarn.unitMeterage || 1))
    );
    // G12: slots sharing one colour pool their metres, rounded up once
    const groupUnits = slots => Math.ceil(
      slots.reduce((sum, s) => sum + (parseFloat(metresRow[s]) || 0), 0) / (yarn.unitMeterage || 1));
    const fams = familiesForYarn(data, yarn);

    // G4: a colour only qualifies for a slot if its buddy strand can be
    // covered too (buddy list, translation, then pairing) — passed to the
    // engine as slotOk, so shifting routes around uncoverable colours
    const slotOk = hasBuddy ? (fam, entry, i, slots) => {
      const v = yarn.variants[entry.name];
      if (!v) return false;
      // G11: a shared colour's buddy must cover all its slots together
      const metres = slots && slots.length > 1 ? slots.map(s => buddyRow[s]) : buddyRow[i];
      return !!resolveBuddy(data, v, entry.name, metres);
    } : null;

    // diagnostic: the two ways a yarn quietly starves the generator
    if (unitsPerSlot.some(u => u > 50) && (yarn.unitMeterage || 1) <= 1) {
      console.warn('[kit-generator] "' + yarn.title + '": unitMeterage=' + yarn.unitMeterage +
        ' with meterage row [' + metresRow.join(';') + '] — requirements computed as ' +
        unitsPerSlot.join('/') + ' BALLS per slot. The custom.meterage metafield probably ' +
        'did not reach the page for this product; almost nothing will qualify.');
    }
    if (hasBuddy) {
      let mainPass = 0, buddyPass = 0;
      for (const t of Object.keys(fams)) {
        for (const c of fams[t].swatch) {
          if (c.stock >= (unitsPerSlot[0] || 1)) {
            mainPass++;
            if (slotOk(fams[t], c, 0)) buddyPass++;
          }
        }
      }
      console.debug('[kit-generator] "' + yarn.title + '" slot-0 qualification: ' +
        mainPass + ' colour(s) pass main stock, ' + buddyPass + ' also pass the buddy strand');
      if (mainPass > 0 && buddyPass === 0) {
        console.warn('[kit-generator] "' + yarn.title + '": every stocked colour FAILS buddy ' +
          'qualification — buddy variant titles do not match the main colour names exactly, ' +
          'no colour_match_group translation resolved (check the group entries + sync), ' +
          'and no mohair_pairing entries resolved. Nothing can qualify.');
      }
    }

    for (const p of parsed) {
      for (const title of targets) {                        // G2 (revised)
        let r;
        try { r = Engine.resolveHardReference(p.recipe, fams[title], unitsPerSlot, { slotOk, groupUnits }); }
        catch (e) { console.warn('kit-generator: recipe "' + p.recipe + '" skipped —', e.message); continue; }
        if (!r) {
          console.debug('[kit-generator] "' + p.recipe + '" × "' + title + '" on "' + yarn.title +
            '": fewer usable colours than slots (stock, buddy coverage, or this colourway is for another base) — skipped.');
        } else {
          pushSet(r, yarn, title, p.recipeIdx, unitsPerSlot);
        }
      }
    }
  }

  // G6 (revised): base colourway listing order, then recipe order, then price
  const baseIndex = new Map((data.baseColourways || []).map((t, i) => [t, i]));
  out.sort((a, b) =>
    ((baseIndex.get(a.base) ?? 999) - (baseIndex.get(b.base) ?? 999)) ||
    (a.recipeIdx - b.recipeIdx) ||
    (a.price - b.price));

  // G5: dedupe after the sort so the first-listed duplicate wins
  const seen = new Set();
  const deduped = [];
  for (const s of out) {
    const key = s.yarn.productId + '|' + s.colours.map(c => c.variantId).join(',');
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(s);
  }
  return deduped;
}

const KitGenerator = { generateSets, resolveBuddy, familiesForYarn, autoName, groupLabelsFor, poolNeeds, kitLines };

if (typeof module !== 'undefined' && module.exports) module.exports = KitGenerator;
if (typeof window !== 'undefined') window.KitGenerator = KitGenerator;